/**
 * GolfGreenSvg — Data-science grade green + approach visualization.
 *
 * Strokes Gained distance bands (Mark Broadie / PGA Tour baseline).
 * Putting distance rings from pin (3ft / 10ft / 20ft / 30ft).
 * Approach distance bands: inside 50y, 50-100y, 100-150y, 150-200y.
 */

import styles from "./SportSvg.module.css";

/* ── Approach distance bands ── */
const APPROACH_BANDS = [
  {
    label: "< 50y",
    y: 200,
    h: 40,
    pct: 82,
    color: "#34c759",
    strokes: "2.60",
  },
  {
    label: "50–100y",
    y: 240,
    h: 48,
    pct: 68,
    color: "#66bb6a",
    strokes: "2.75",
  },
  {
    label: "100–150y",
    y: 288,
    h: 52,
    pct: 55,
    color: "#ffcc00",
    strokes: "2.92",
  },
  {
    label: "150–200y",
    y: 340,
    h: 55,
    pct: 38,
    color: "#ff9500",
    strokes: "3.15",
  },
];

/* ── Putting rings from pin ── */
const PUTT_RINGS = [
  { r: 12, label: "3ft", pct: 99, color: "#34c759" },
  { r: 30, label: "10ft", pct: 55, color: "#66bb6a" },
  { r: 52, label: "20ft", pct: 30, color: "#ffcc00" },
  { r: 75, label: "30ft+", pct: 12, color: "#ff9500" },
];

/* Pin position on green */
const PIN_X = 240;
const PIN_Y = 100;

export function GolfGreenSvg() {
  return (
    <svg
      viewBox="0 0 500 460"
      className={styles.svg}
      role="img"
      aria-label="Golf green and approach zones"
    >
      <title>Golf green with putting rings and approach distance bands (Strokes Gained)</title>
      <defs>
        {/* Green texture */}
        <radialGradient id="gg-green" cx="50%" cy="45%" r="50%">
          <stop offset="0%" stopColor="rgba(52,199,89,0.12)" />
          <stop offset="100%" stopColor="rgba(52,199,89,0.03)" />
        </radialGradient>
        {/* Fairway texture */}
        <linearGradient id="gg-fairway" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(76,175,80,0.06)" />
          <stop offset="100%" stopColor="rgba(76,175,80,0.02)" />
        </linearGradient>
      </defs>

      {/* ── Approach / fairway area ── */}
      <rect x="60" y="195" width="380" height="250" rx="4" fill="url(#gg-fairway)" />

      {/* ── Approach distance bands ── */}
      {APPROACH_BANDS.map((band, i) => (
        <g key={band.label}>
          {/* Band fill */}
          <rect
            x="80"
            y={band.y}
            width="340"
            height={band.h}
            rx="2"
            fill={`${band.color}08`}
            stroke={`${band.color}18`}
            strokeWidth="1"
          />
          {/* Distance line */}
          <line
            x1="80"
            y1={band.y}
            x2="420"
            y2={band.y}
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="1"
            strokeDasharray={i > 0 ? "4,4" : "0"}
          />
          {/* Band label (left) */}
          <text
            x="68"
            y={band.y + band.h / 2 + 4}
            textAnchor="end"
            className={styles.zoneLabel}
            fill="rgba(255,255,255,0.35)"
          >
            {band.label}
          </text>
          {/* GIR % (center) */}
          <text
            x="250"
            y={band.y + band.h / 2 + 5}
            textAnchor="middle"
            className={styles.zonePct}
            fill={band.color}
          >
            GIR {band.pct}%
          </text>
          {/* Expected strokes (right) */}
          <text
            x="432"
            y={band.y + band.h / 2 + 4}
            className={styles.zoneLabel}
            fill="rgba(255,255,255,0.3)"
          >
            E[S]={band.strokes}
          </text>
        </g>
      ))}

      {/* ── Green shape ── */}
      {/* Fringe (outer) */}
      <ellipse
        cx="250"
        cy="110"
        rx="165"
        ry="100"
        fill="none"
        stroke="rgba(52,199,89,0.12)"
        strokeWidth="1"
        strokeDasharray="6,4"
      />
      <text x="420" y="95" className={styles.zoneLabel} fill="rgba(255,255,255,0.25)">
        Fringe
      </text>

      {/* Green surface */}
      <ellipse
        cx="250"
        cy="110"
        rx="140"
        ry="82"
        fill="url(#gg-green)"
        stroke="rgba(52,199,89,0.3)"
        strokeWidth="2"
      />
      <text
        x="250"
        y="26"
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="rgba(52,199,89,0.5)"
      >
        GREEN
      </text>

      {/* ── Putting distance rings ── */}
      {PUTT_RINGS.map((ring) => (
        <g key={ring.label}>
          <circle
            cx={PIN_X}
            cy={PIN_Y}
            r={ring.r}
            fill="none"
            stroke={`${ring.color}20`}
            strokeWidth="1"
            strokeDasharray="3,3"
          />
          {/* Distance label on ring */}
          <text x={PIN_X + ring.r + 4} y={PIN_Y - 2} className={styles.zoneLabel} fill={ring.color}>
            {ring.label}
          </text>
          {/* Make % inside ring */}
          {ring.r >= 30 && (
            <text
              x={PIN_X}
              y={PIN_Y + ring.r - 6}
              textAnchor="middle"
              className={styles.zonePct}
              fill={ring.color}
            >
              {ring.pct}%
            </text>
          )}
        </g>
      ))}
      {/* Innermost ring label */}
      <text
        x={PIN_X}
        y={PIN_Y + 18}
        textAnchor="middle"
        className={styles.zoneLabel}
        fill="#34c759"
      >
        99%
      </text>

      {/* ── Pin with flag ── */}
      <line
        x1={PIN_X}
        y1={PIN_Y - 40}
        x2={PIN_X}
        y2={PIN_Y}
        stroke="rgba(255,255,255,0.6)"
        strokeWidth="1.5"
      />
      <polygon
        points={`${PIN_X},${PIN_Y - 40} ${PIN_X + 14},${PIN_Y - 34} ${PIN_X},${PIN_Y - 28}`}
        fill="#ff3b30"
        opacity="0.85"
      />
      {/* Hole */}
      <circle
        cx={PIN_X}
        cy={PIN_Y}
        r="3"
        fill="rgba(20,20,20,0.8)"
        stroke="rgba(255,255,255,0.5)"
        strokeWidth="1"
      />

      {/* ── Shot landing indicators ── */}
      {/* On green — made putts (filled) */}
      <circle cx={PIN_X + 8} cy={PIN_Y + 5} r="4" fill="#34c759" opacity="0.9" />
      <circle cx={PIN_X - 20} cy={PIN_Y + 18} r="4" fill="#34c759" opacity="0.9" />
      <circle cx={PIN_X + 35} cy={PIN_Y - 15} r="4" fill="#66bb6a" opacity="0.85" />
      {/* Missed putts */}
      <circle
        cx={PIN_X - 45}
        cy={PIN_Y + 30}
        r="4"
        fill="none"
        stroke="#ffcc00"
        strokeWidth="1.5"
        opacity="0.8"
      />
      <circle
        cx={PIN_X + 60}
        cy={PIN_Y + 40}
        r="4"
        fill="none"
        stroke="#ff9500"
        strokeWidth="1.5"
        opacity="0.8"
      />
      {/* Approach shots in fairway */}
      <circle cx="200" cy="225" r="4" fill="#34c759" opacity="0.85" />
      <circle cx="290" cy="265" r="4" fill="#66bb6a" opacity="0.85" />
      <circle
        cx="310"
        cy="315"
        r="4"
        fill="none"
        stroke="#ff9500"
        strokeWidth="1.5"
        opacity="0.8"
      />
      <circle
        cx="180"
        cy="370"
        r="4"
        fill="none"
        stroke="#ff3b30"
        strokeWidth="1.5"
        opacity="0.8"
      />

      {/* ── Player position ── */}
      <g>
        <circle
          cx="250"
          cy="430"
          r="12"
          fill="rgba(33,150,243,0.12)"
          stroke="#2196f3"
          strokeWidth="2"
        />
        <circle cx="250" cy="423" r="5" fill="#2196f3" opacity="0.85" />
        {/* Club */}
        <line
          x1="258"
          y1="428"
          x2="270"
          y2="418"
          stroke="#2196f3"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <text x="250" y="455" textAnchor="middle" className={styles.zoneLabel} fill="#2196f3">
          PLAYER
        </text>
      </g>

      {/* ── Approach trajectory ── */}
      <line
        x1="250"
        y1="422"
        x2={PIN_X}
        y2={PIN_Y + 10}
        stroke="rgba(33,150,243,0.2)"
        strokeWidth="1.5"
        strokeDasharray="6,4"
      />
      <polygon
        points={`${PIN_X},${PIN_Y + 10} ${PIN_X - 4},${PIN_Y + 22} ${PIN_X + 4},${PIN_Y + 22}`}
        fill="rgba(33,150,243,0.35)"
      />

      {/* ── Legend ── */}
      <g>
        <text x="16" y="30" className={styles.zoneLabel} fill="rgba(255,255,255,0.35)">
          Strokes
        </text>
        <text x="16" y="40" className={styles.zoneLabel} fill="rgba(255,255,255,0.35)">
          Gained
        </text>
        <rect x="12" y="48" width="8" height="8" rx="1" fill="#34c759" opacity="0.4" />
        <text x="24" y="56" className={styles.zoneLabel} fill="rgba(255,255,255,0.3)">
          &gt;80%
        </text>
        <rect x="12" y="62" width="8" height="8" rx="1" fill="#66bb6a" opacity="0.4" />
        <text x="24" y="70" className={styles.zoneLabel} fill="rgba(255,255,255,0.3)">
          60-80%
        </text>
        <rect x="12" y="76" width="8" height="8" rx="1" fill="#ffcc00" opacity="0.4" />
        <text x="24" y="84" className={styles.zoneLabel} fill="rgba(255,255,255,0.3)">
          40-60%
        </text>
        <rect x="12" y="90" width="8" height="8" rx="1" fill="#ff9500" opacity="0.4" />
        <text x="24" y="98" className={styles.zoneLabel} fill="rgba(255,255,255,0.3)">
          &lt;40%
        </text>
      </g>
    </svg>
  );
}
